// widget/src/components/BottomSheet.tsx
//
// Phase 2 mini-cart — mobile bottom sheet.
//
// Three states ("snap points") for the sheet height:
//   - peek  : 60-72px chip above the composer. Persistent; tap handle to half.
//   - half  : 60% viewport height. The default "engaged" view.
//   - full  : 95% viewport height. Reading-mode for long basket / payloads.
//
// Drag handle at the top of the sheet drives state transitions:
//   - Swipe up   → next state (peek → half → full).
//   - Swipe down → prev state (full → half → peek).
//   - Tap        → toggle peek ↔ half (matches Apple Maps-style affordance).
//
// Gesture handling uses pointer events (works on touch + mouse). Overscroll
// past the end snap simply snaps back via the same handler.
//
// The component owns its own state but exposes `onStateChange` so the parent
// can observe transitions (e.g., dim the page when the sheet goes "full").
//
// Renders `peekContent` inside the body when state === "peek" (the dock chip
// strip); renders `children` when state is "half" or "full" (the actual sheet
// body). This split keeps the chip + body tap targets distinct.

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react"
import styles from "../styles/widgetFin.module.css"

export type BottomSheetState = "peek" | "half" | "full"

export interface BottomSheetHandle {
  /** Programmatically jump to a specific state — e.g., basket dock chip tap. */
  expand: (state: BottomSheetState) => void
  state: BottomSheetState
}

export interface BottomSheetProps {
  children: ReactNode
  /** Content shown in the peek-state pill (e.g., basket count chip). */
  peekContent: ReactNode
  /** Starting snap point. Defaults to "peek" so the sheet doesn't blanket the
   *  chat unless Pearl/the customer asks for it. */
  initialState?: BottomSheetState
  /** Observer for state changes — parent can sync analytics or modify
   *  surrounding UI (e.g., dim scrim when full). */
  onStateChange?: (state: BottomSheetState) => void
}

// State machine — index in this list is the "elevation" of a snap point.
const STATES: BottomSheetState[] = ["peek", "half", "full"]
// Pixel distance threshold for swipe gestures vs. taps. Below this we treat
// the press as a tap (handle toggle); above, as a directional swipe.
const SWIPE_THRESHOLD_PX = 16

export const BottomSheet = forwardRef<BottomSheetHandle, BottomSheetProps>(
  function BottomSheet(
    { children, peekContent, initialState = "peek", onStateChange },
    ref,
  ) {
    const [state, setState] = useState<BottomSheetState>(initialState)
    const stateRef = useRef(state)
    stateRef.current = state

    // Pointer-drag tracking for the handle. We capture pointerdown on the
    // handle, then read pointermove / pointerup on the same element via
    // setPointerCapture so the gesture survives moving off-element.
    const dragStartY = useRef<number | null>(null)
    const dragDeltaY = useRef(0)

    const goToState = useCallback(
      (next: BottomSheetState) => {
        setState((prev) => {
          if (prev === next) return prev
          onStateChange?.(next)
          return next
        })
      },
      [onStateChange],
    )

    useImperativeHandle(
      ref,
      () => ({
        expand: goToState,
        state,
      }),
      [goToState, state],
    )

    const onHandlePointerDown = useCallback(
      (e: ReactPointerEvent<HTMLDivElement>) => {
        dragStartY.current = e.clientY
        dragDeltaY.current = 0
        try {
          // Capture so subsequent pointermove fires on this element even if
          // the finger drifts off the 40x4px handle hit area.
          e.currentTarget.setPointerCapture(e.pointerId)
        } catch {
          /* Safari can throw if not over a primary pointer; safe to ignore. */
        }
      },
      [],
    )

    const onHandlePointerMove = useCallback(
      (e: ReactPointerEvent<HTMLDivElement>) => {
        if (dragStartY.current == null) return
        dragDeltaY.current = e.clientY - dragStartY.current
      },
      [],
    )

    const onHandlePointerUp = useCallback(
      (e: ReactPointerEvent<HTMLDivElement>) => {
        const startY = dragStartY.current
        dragStartY.current = null
        const dy = dragDeltaY.current
        dragDeltaY.current = 0
        try {
          e.currentTarget.releasePointerCapture(e.pointerId)
        } catch {
          /* ignore — pointer may have already been released */
        }
        if (startY == null) return

        const idx = STATES.indexOf(stateRef.current)
        // Tap (small motion) → toggle peek ↔ half. Lets one-handed users
        // promote the chip without a swipe.
        if (Math.abs(dy) < SWIPE_THRESHOLD_PX) {
          const next: BottomSheetState =
            stateRef.current === "peek" ? "half" : "peek"
          goToState(next)
          return
        }
        // Swipe up (negative dy) → next snap; swipe down (positive) → prev.
        // Clamping to [0, STATES.length - 1] absorbs over-swipes silently.
        const nextIdx =
          dy < 0
            ? Math.min(idx + 1, STATES.length - 1)
            : Math.max(idx - 1, 0)
        goToState(STATES[nextIdx])
      },
      [goToState],
    )

    // Keyboard fallback for the handle — Up/Down promote/demote one step.
    // Covers the screen-reader accessibility gap noted in the spec.
    const onHandleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        const idx = STATES.indexOf(state)
        if (e.key === "ArrowUp") {
          e.preventDefault()
          goToState(STATES[Math.min(idx + 1, STATES.length - 1)])
        } else if (e.key === "ArrowDown") {
          e.preventDefault()
          goToState(STATES[Math.max(idx - 1, 0)])
        } else if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          goToState(state === "peek" ? "half" : "peek")
        }
      },
      [state, goToState],
    )

    // Sync initialState → state if the prop changes (rare, but supported).
    useEffect(() => {
      setState(initialState)
    }, [initialState])

    return (
      <div
        className={styles["bottomSheet"]}
        data-state={state}
        role="region"
        aria-label="Pearl basket sheet"
      >
        <div
          className={styles["bottomSheetHandle"]}
          role="button"
          tabIndex={0}
          aria-label="Drag to resize"
          aria-valuetext={state}
          onPointerDown={onHandlePointerDown}
          onPointerMove={onHandlePointerMove}
          onPointerUp={onHandlePointerUp}
          onPointerCancel={onHandlePointerUp}
          onKeyDown={onHandleKeyDown}
        />
        {state === "peek" ? (
          <div className={styles["bottomSheetPeek"]}>{peekContent}</div>
        ) : (
          <div className={styles["bottomSheetBody"]}>{children}</div>
        )}
      </div>
    )
  },
)
